import { createSensorController } from "./sensors.js";
import type { SensorController, SensorStatus } from "./sensors.js";

export interface SensorControlsOptions {
  readonly controllerFactory?: (
    onStatusChange: (status: SensorStatus) => void,
  ) => SensorController;
}

export interface SensorControls {
  readonly controller: SensorController;
  readonly element: HTMLElement;
  destroy(): Promise<void>;
}

export type SensorControlsCleanupPhase =
  | "enable-listener"
  | "off-listener"
  | "controller"
  | "element";

export interface SensorControlsCleanupFailure {
  readonly phase: SensorControlsCleanupPhase;
  readonly cause: unknown;
}

export class SensorControlsCleanupError extends AggregateError {
  readonly failures: readonly SensorControlsCleanupFailure[];

  constructor(failures: readonly SensorControlsCleanupFailure[]) {
    const ownedFailures = Object.freeze(failures.map((failure) => Object.freeze({ ...failure })));
    super(
      ownedFailures.map((failure) => failure.cause),
      `Sensor controls cleanup incomplete: ${ownedFailures.map((failure) => failure.phase).join(", ")}`,
    );
    this.name = "SensorControlsCleanupError";
    this.failures = ownedFailures;
  }
}

const STATUS_LABELS: Readonly<Record<SensorStatus, string>> = Object.freeze({
  off: "Off",
  requesting: "Requesting",
  enabled: "Enabled",
  denied: "Denied",
  unavailable: "Unavailable",
  revoked: "Revoked",
});

const HEADING_COUNTERS = new WeakMap<Document, number>();

function nextHeadingId(document: Document): string {
  const next = (HEADING_COUNTERS.get(document) ?? 0) + 1;
  HEADING_COUNTERS.set(document, next);
  return `sensor-controls-heading-${next}`;
}

export function mountSensorControls(
  container: HTMLElement,
  options: SensorControlsOptions = {},
): SensorControls {
  const document = container.ownerDocument;
  const section = document.createElement("section");
  const heading = document.createElement("h2");
  const statusLine = document.createElement("p");
  const statusLabel = document.createElement("span");
  const statusOutput = document.createElement("output");
  const actions = document.createElement("div");
  const enableButton = document.createElement("button");
  const offButton = document.createElement("button");
  let destroyRequested = false;
  let operationGeneration = 0;
  let enableListenerOwned = true;
  let offListenerOwned = true;
  let controllerOwned = true;
  let elementOwned = true;
  let activeDestroy: Promise<void> | null = null;
  let completedDestroy: Promise<void> | null = null;

  const headingId = nextHeadingId(document);
  section.setAttribute("aria-labelledby", headingId);
  heading.id = headingId;
  heading.textContent = "Motion ambience";
  statusLabel.textContent = "Status: ";
  statusOutput.setAttribute("aria-live", "polite");
  statusOutput.setAttribute("aria-atomic", "true");
  enableButton.type = "button";
  enableButton.textContent = "Enable";
  offButton.type = "button";
  offButton.textContent = "Off";

  const renderStatus = (status: SensorStatus): void => {
    if (!destroyRequested) statusOutput.textContent = STATUS_LABELS[status];
  };
  const controllerFactory = options.controllerFactory
    ?? ((onStatusChange: (status: SensorStatus) => void): SensorController =>
      createSensorController({ onStatusChange }));
  const controller = controllerFactory(renderStatus);

  const safeStatus = (fallback: SensorStatus): SensorStatus => {
    try {
      return controller.status();
    } catch {
      return fallback;
    }
  };

  const handleEnable = (): void => {
    if (destroyRequested) return;
    const operation = ++operationGeneration;
    let result: Promise<SensorStatus>;
    try {
      result = controller.enableFromGesture();
    } catch {
      renderStatus(safeStatus("denied"));
      return;
    }
    void Promise.resolve(result).then(
      () => {
        if (!destroyRequested && operationGeneration === operation) {
          renderStatus(safeStatus("denied"));
        }
      },
      () => {
        if (!destroyRequested && operationGeneration === operation) {
          renderStatus(safeStatus("denied"));
        }
      },
    ).catch(() => {
      // Rendering failures cannot leak an unhandled operation promise.
    });
  };

  const handleOff = (): void => {
    if (destroyRequested) return;
    operationGeneration += 1;
    try {
      controller.disable();
    } catch {
      // The visible state still comes from the controller's surviving authority.
    }
    renderStatus(safeStatus("off"));
  };

  enableButton.addEventListener("click", handleEnable);
  offButton.addEventListener("click", handleOff);
  renderStatus(safeStatus("off"));
  statusLine.append(statusLabel, statusOutput);
  actions.append(enableButton, offButton);
  section.append(heading, statusLine, actions);
  container.append(section);

  const destroy = (): Promise<void> => {
    if (completedDestroy !== null) return completedDestroy;
    if (activeDestroy !== null) return activeDestroy;

    if (!destroyRequested) {
      destroyRequested = true;
      operationGeneration += 1;
    }

    let resolveDestroy: () => void = () => undefined;
    let rejectDestroy: (reason: SensorControlsCleanupError) => void = () => undefined;
    const promise = new Promise<void>((resolve, reject) => {
      resolveDestroy = resolve;
      rejectDestroy = reject;
    });
    activeDestroy = promise;
    const failures: SensorControlsCleanupFailure[] = [];

    if (enableListenerOwned) {
      try {
        enableButton.removeEventListener("click", handleEnable);
        enableListenerOwned = false;
      } catch (cause) {
        failures.push({ phase: "enable-listener", cause });
      }
    }
    if (offListenerOwned) {
      try {
        offButton.removeEventListener("click", handleOff);
        offListenerOwned = false;
      } catch (cause) {
        failures.push({ phase: "off-listener", cause });
      }
    }
    if (controllerOwned) {
      try {
        controller.disable();
        controllerOwned = false;
      } catch (cause) {
        failures.push({ phase: "controller", cause });
      }
    }
    if (elementOwned) {
      try {
        section.remove();
        elementOwned = false;
      } catch (cause) {
        failures.push({ phase: "element", cause });
      }
    }

    if (failures.length === 0) resolveDestroy();
    else rejectDestroy(new SensorControlsCleanupError(failures));

    void promise.then(
      () => {
        completedDestroy = promise;
        if (activeDestroy === promise) activeDestroy = null;
      },
      () => {
        if (activeDestroy === promise) activeDestroy = null;
      },
    );
    return promise;
  };

  return Object.freeze({ controller, element: section, destroy });
}
